/*
 * Contenu éditorial des pages de soins — version française (référence).
 * Une page = une intention de recherche. Les textes sont rédigés pour la
 * patiente : pas de promesse de résultat, pas de tarif, pas d'affirmation
 * non vérifiable. Les adresses sont définies dans i18n/config.js.
 */

const soinsFr = {
    sopk: {
        nav: "SOPK",
        title: "SOPK : consultation médicale à Asnières-sur-Seine",
        description:
            "Syndrome des ovaires polykystiques : bilan hormonal, acné, hirsutisme et chute de cheveux. Consultation du Dr Souhali Massilia à Asnières-sur-Seine (92).",
        eyebrow: "Troubles hormonaux",
        h1: "SOPK : comprendre, puis traiter ce qui se voit",
        lead: "Le syndrome des ovaires polykystiques touche environ une femme sur dix. Au cabinet d'Asnières-sur-Seine, la consultation cherche d'abord la cause du déséquilibre, puis prend en charge ses effets visibles : acné, pilosité, chute de cheveux.",
        photo: { file: "bureau.jpg", alt: "Le bureau de consultation du cabinet à Asnières-sur-Seine" },
        condition: "Syndrome des ovaires polykystiques",
        sections: [
            {
                h2: "Reconnaître un SOPK",
                paragraphes: [
                    "Le SOPK est un déséquilibre hormonal, pas une maladie des ovaires au sens strict. Il associe, à des degrés très variables, une production trop importante d'androgènes, une ovulation irrégulière et parfois une résistance à l'insuline. C'est cette combinaison qui explique des signes en apparence sans rapport les uns avec les autres.",
                    "Beaucoup de femmes vivent des années avec un SOPK sans le savoir : on leur a traité l'acné, puis la pilosité, puis la chute de cheveux, chacun de son côté, sans jamais relier les signes entre eux.",
                ],
                puces: [
                    "cycles irréguliers, espacés ou absents",
                    "acné persistante à l'âge adulte, surtout sur le bas du visage",
                    "pilosité excessive sur le visage, le cou, le thorax ou l'abdomen (hirsutisme)",
                    "cheveux qui s'affinent sur le dessus du crâne",
                    "prise de poids difficile à expliquer, fatigue après les repas",
                    "difficultés à concevoir",
                ],
            },
            {
                h2: "Le bilan au cabinet",
                paragraphes: [
                    "La première consultation commence par un entretien détaillé : histoire des cycles, antécédents familiaux, traitements déjà essayés, retentissement au quotidien. L'examen clinique évalue la peau, la répartition de la pilosité et la densité capillaire.",
                    "Un bilan hormonal sanguin est ensuite prescrit, complété si besoin d'un bilan métabolique et d'une échographie pelvienne. L'objectif n'est pas d'accumuler les examens, mais de confirmer le diagnostic et d'éliminer les autres causes possibles d'un excès d'androgènes.",
                    "Les résultats sont expliqués lors d'une consultation de synthèse : ce que chaque valeur signifie, ce qui est en cause chez vous, et ce qui peut être amélioré.",
                ],
            },
            {
                h2: "Les prises en charge possibles",
                paragraphes: [
                    "La prise en charge est toujours construite avec vous, en fonction de ce qui vous gêne le plus et de votre projet de vie — y compris un éventuel projet de grossesse, qui change les options thérapeutiques.",
                    "Elle associe, selon les situations, un traitement médical du déséquilibre hormonal, des conseils d'hygiène de vie ciblés sur la résistance à l'insuline, et un traitement des signes visibles : épilation laser ou électrolyse pour l'hirsutisme, protocole pour l'acné, mésothérapie du cuir chevelu et traitement de fond pour la chute de cheveux.",
                    "Le suivi est régulier : un déséquilibre hormonal ne se corrige pas en une séance, et les traitements se réévaluent au fil des mois.",
                ],
            },
        ],
        faq: [
            [
                "Le SOPK se guérit-il ?",
                "On ne « guérit » pas d'un SOPK comme d'une infection : c'est un terrain hormonal, qui évolue avec l'âge et le mode de vie. En revanche, ses manifestations — acné, pilosité, cycles irréguliers, chute de cheveux — se contrôlent très souvent bien lorsqu'elles sont prises en charge de façon cohérente.",
            ],
            [
                "Faut-il arrêter la pilule pour faire le bilan ?",
                "Pas nécessairement, et surtout pas de votre propre initiative. Certains dosages hormonaux sont ininterprétables sous contraception œstroprogestative : le médecin vous indiquera, selon votre situation, s'il faut adapter le moment du bilan.",
            ],
            [
                "Puis-je consulter si mon diagnostic est déjà posé ?",
                "Oui, c'est même fréquent. Beaucoup de patientes viennent avec un diagnostic connu mais des signes cutanés ou capillaires jamais traités, ou un traitement qu'elles supportent mal.",
            ],
        ],
        related: ["chute-de-cheveux", "epilation-laser", "menopause"],
    },

    menopause: {
        nav: "Ménopause",
        title: "Ménopause et périménopause : suivi à Asnières-sur-Seine",
        description:
            "Périménopause et ménopause : peau plus fine, relâchement, sécheresse intime, chute de cheveux. Consultation médicale à Asnières-sur-Seine (92).",
        eyebrow: "Santé de la femme",
        h1: "Périménopause et ménopause : un suivi, pas une fatalité",
        lead: "La baisse des œstrogènes ne se joue pas seulement sur les cycles : elle se lit sur la peau, les cheveux, la silhouette et le confort intime. Au cabinet d'Asnières-sur-Seine, ces changements sont pris au sérieux et suivis dans leur ensemble.",
        photo: { file: "cafe.jpg", alt: "L'espace d'accueil du cabinet, avec un coin café" },
        condition: "Ménopause",
        sections: [
            {
                h2: "Ce qui change, et pourquoi",
                paragraphes: [
                    "La périménopause commence souvent plusieurs années avant l'arrêt définitif des règles. Les cycles se dérèglent, le sommeil se fragmente, l'humeur varie — et en parallèle, la peau se modifie. La production de collagène diminue nettement dans les premières années suivant la ménopause : la peau s'affine, se déshydrate, perd de sa fermeté.",
                    "Le rapport entre œstrogènes et androgènes se modifie également, ce qui explique l'apparition de poils plus épais sur le menton ou la lèvre supérieure chez certaines femmes, en même temps qu'un affinement des cheveux.",
                ],
                puces: [
                    "peau plus sèche, plus fine, qui marque davantage",
                    "perte de fermeté de l'ovale du visage et du cou",
                    "cheveux moins denses, plus cassants",
                    "poils plus épais sur le visage",
                    "sécheresse et inconfort intimes",
                    "taches pigmentaires plus visibles",
                ],
            },
            {
                h2: "La consultation",
                paragraphes: [
                    "L'entretien fait le point sur vos symptômes, vos antécédents personnels et familiaux, vos traitements en cours et ce qui vous pèse le plus au quotidien. L'examen clinique évalue la qualité de la peau, la densité capillaire et l'état des muqueuses si vous le souhaitez.",
                    "Un bilan biologique peut être proposé. Les indications et les contre-indications d'un éventuel traitement hormonal de la ménopause sont expliquées sans raccourci : ce traitement ne convient pas à toutes, et la décision se prend après évaluation de votre situation personnelle.",
                ],
            },
            {
                h2: "Ce qui peut être proposé",
                paragraphes: [
                    "Côté peau, les soins visent à soutenir la qualité tissulaire : mésothérapie, skinboosters, microneedling, peelings adaptés aux peaux matures, inducteurs de collagène, injections d'acide hyaluronique lorsque le relâchement le justifie.",
                    "Côté cheveux, le bilan de la chute oriente vers une mésothérapie du cuir chevelu et un traitement de fond. Côté confort intime, la sécheresse et l'atrophie font l'objet d'une prise en charge spécifique, à aborder sans gêne : c'est un motif de consultation fréquent et traitable.",
                    "Chaque proposition est hiérarchisée, expliquée, et fait l'objet d'un devis écrit lorsqu'il s'agit d'un acte esthétique, avec un délai de réflexion.",
                ],
            },
        ],
        faq: [
            [
                "À partir de quand consulter ?",
                "Dès que les changements vous gênent, sans attendre l'arrêt complet des règles. La périménopause est justement la période où une prise en charge précoce de la peau et des cheveux a le plus d'intérêt.",
            ],
            [
                "Faut-il obligatoirement un traitement hormonal ?",
                "Non. Le traitement hormonal de la ménopause est une option, soumise à des indications et à des contre-indications précises. Beaucoup de femmes sont suivies et soulagées sans y recourir.",
            ],
            [
                "La sécheresse intime peut-elle vraiment être traitée ?",
                "Oui. C'est l'un des symptômes les plus fréquents et les moins rapportés par les patientes, alors qu'il existe des prises en charge efficaces. La consultation permet d'évaluer la situation et de proposer une solution adaptée.",
            ],
        ],
        related: ["sopk", "chute-de-cheveux", "melasma"],
    },

    "epilation-laser": {
        nav: "Épilation laser",
        title: "Épilation laser et électrolyse à Asnières-sur-Seine",
        description:
            "Épilation laser du visage et du corps, électrolyse des poils clairs, prise en charge de l'hirsutisme. Cabinet médical à Asnières-sur-Seine (92).",
        eyebrow: "Pilosité",
        h1: "Épilation laser et électrolyse, par un médecin",
        lead: "Au cabinet d'Asnières-sur-Seine, l'épilation n'est pas un geste isolé : lorsque la pilosité est excessive, on cherche aussi d'où elle vient. Laser et électrolyse sont pratiqués sur place, et souvent de façon complémentaire.",
        photo: { file: "salle.jpg", alt: "La salle de soins équipée pour l'épilation laser" },
        condition: "Hirsutisme",
        sections: [
            {
                h2: "Laser ou électrolyse ?",
                paragraphes: [
                    "Le laser agit sur la mélanine du poil : il est très efficace sur les poils foncés et permet de traiter rapidement de grandes surfaces, comme les jambes, les aisselles ou le maillot. En revanche, il n'a aucune prise sur un poil sans pigment.",
                    "L'électrolyse détruit le follicule poil par poil, indépendamment de sa couleur. Elle est donc indiquée sur les poils blancs, blonds, roux ou résistants, et pour les finitions sur de petites zones comme le menton ou le contour des lèvres.",
                    "Les deux techniques ne s'opposent pas : il est fréquent de traiter l'essentiel au laser, puis de terminer à l'électrolyse les poils que le laser ne voit pas. Le choix se fait après examen de la zone, du type de poil et de votre phototype.",
                ],
            },
            {
                h2: "Pourquoi consulter un médecin",
                paragraphes: [
                    "Une pilosité qui s'installe ou qui s'aggrave n'est pas toujours un simple trait familial. Elle peut révéler un excès d'androgènes — SOPK, trouble androgénique, effet d'un traitement. Dans ce cas, épiler sans chercher la cause revient à traiter le signe en laissant le mécanisme en place.",
                    "Le médecin évalue l'étendue de la pilosité, recherche les signes associés (acné, cycles irréguliers, chute de cheveux) et prescrit un bilan hormonal si nécessaire. La prise en charge de la cause rend ensuite l'épilation plus durable.",
                    "La consultation est aussi le moment de vérifier les contre-indications, d'adapter les réglages à votre peau et de poser le cadre : nombre de séances prévisible, intervalle entre les séances, précautions avant et après.",
                ],
            },
            {
                h2: "Le déroulé des séances",
                paragraphes: [
                    "Les séances sont espacées de plusieurs semaines pour suivre le cycle de pousse du poil : seuls les follicules en phase active répondent au traitement, ce qui explique qu'un cycle complet demande plusieurs séances.",
                    "La zone doit être rasée la veille, non épilée à la cire ni à la pince dans les semaines précédentes, et protégée du soleil avant comme après. Les suites sont habituellement simples : une rougeur transitoire est fréquente dans les heures qui suivent.",
                    "Le nombre de séances dépend de la zone, de la densité, de la couleur du poil et de la cause de la pilosité. Il est estimé en consultation, jamais promis à l'avance.",
                ],
            },
        ],
        faq: [
            [
                "L'épilation laser est-elle définitive ?",
                "On parle d'épilation durable plutôt que définitive. La majorité des poils traités ne repoussent pas, mais un terrain hormonal actif peut réactiver des follicules : des séances d'entretien espacées sont alors nécessaires.",
            ],
            [
                "Est-ce douloureux ?",
                "La sensation est souvent décrite comme un claquement d'élastique chaud. Elle varie selon la zone et le seuil de chacune. Les réglages et le refroidissement de l'appareil sont adaptés pour rester supportables.",
            ],
            [
                "Peut-on traiter une peau foncée ?",
                "Oui, sous réserve d'un appareil et de réglages adaptés au phototype. C'est précisément l'intérêt d'une évaluation médicale préalable : elle détermine ce qui est réalisable sans risque pour votre peau.",
            ],
        ],
        related: ["sopk", "melasma", "chute-de-cheveux"],
    },

    melasma: {
        nav: "Mélasma",
        title: "Mélasma et taches pigmentaires : médecin à Asnières",
        description:
            "Mélasma, masque de grossesse et taches brunes : protocole dépigmentant, peelings et photoprotection. Cabinet médical à Asnières-sur-Seine (92).",
        eyebrow: "Taches pigmentaires",
        h1: "Mélasma : traiter les taches sans agresser la peau",
        lead: "Le mélasma est une hyperpigmentation tenace, souvent déclenchée par les hormones et entretenue par le soleil. Sa prise en charge est médicale, progressive, et repose autant sur la prévention des récidives que sur le traitement des taches.",
        photo: { file: "peau.jpg", alt: "Détail du grain de peau, illustration du soin de la peau" },
        condition: "Mélasma",
        sections: [
            {
                h2: "Reconnaître un mélasma",
                paragraphes: [
                    "Le mélasma se présente sous forme de plaques brunes à contours irréguliers, symétriques, sur le front, les pommettes, la lèvre supérieure ou le menton. Il est fréquent pendant la grossesse — on parle alors de masque de grossesse — et sous contraception hormonale.",
                    "Il ne doit pas être confondu avec des taches solaires, des taches post-inflammatoires après une acné, ou une autre lésion pigmentée qui relève d'un avis dermatologique. Cette distinction conditionne tout le traitement : c'est la première chose que vérifie la consultation.",
                ],
            },
            {
                h2: "Le protocole de prise en charge",
                paragraphes: [
                    "Le traitement repose sur trois piliers indissociables : une photoprotection stricte et quotidienne, un traitement dépigmentant prescrit et suivi, et des soins réalisés au cabinet — peelings adaptés, parfois microneedling ou mésothérapie selon le type de peau.",
                    "Les gestes trop agressifs sont contre-productifs : sur un mélasma, une inflammation mal maîtrisée relance la pigmentation. Les séances sont donc espacées, progressives, et réévaluées à chaque étape.",
                    "La durée est de plusieurs mois. C'est annoncé dès la première consultation, car la régularité compte davantage que l'intensité des séances.",
                ],
            },
            {
                h2: "Éviter la récidive",
                paragraphes: [
                    "Le mélasma récidive volontiers dès l'arrêt de la protection solaire ou lors d'une reprise de traitement hormonal. L'entretien fait donc partie du traitement : écran solaire élevé toute l'année, y compris par temps couvert et derrière une vitre, et protection mécanique l'été.",
                    "Un point est fait sur votre contraception et vos traitements en cours, car certains favorisent la pigmentation. Cette approche globale est ce qui distingue une prise en charge médicale d'un simple soin esthétique.",
                ],
            },
        ],
        faq: [
            [
                "Le laser est-il indiqué sur un mélasma ?",
                "Le laser n'est pas le traitement de première intention : mal choisi, il peut aggraver un mélasma en relançant la pigmentation. La prise en charge commence par la photoprotection, les dépigmentants et les peelings adaptés.",
            ],
            [
                "Combien de temps avant de voir une différence ?",
                "Les premiers changements s'apprécient généralement après plusieurs semaines de traitement régulier. L'évaluation se fait sur des photographies prises au cabinet, dans les mêmes conditions d'éclairage.",
            ],
            [
                "Puis-je être traitée pendant une grossesse ou un allaitement ?",
                "Certains traitements dépigmentants sont contre-indiqués dans ces situations. La consultation permet d'adapter la prise en charge et, souvent, de se concentrer d'abord sur la protection solaire.",
            ],
        ],
        related: ["menopause", "sopk", "epilation-laser"],
    },

    "chute-de-cheveux": {
        nav: "Chute de cheveux",
        title: "Chute de cheveux : bilan médical à Asnières-sur-Seine",
        description:
            "Chute de cheveux chez la femme : bilan de la cause, mésothérapie du cuir chevelu et traitement de fond. Cabinet médical à Asnières-sur-Seine (92).",
        eyebrow: "Cheveux",
        h1: "Chute de cheveux : chercher la cause avant de traiter",
        lead: "Une chute de cheveux chez la femme a presque toujours une explication : hormonale, carentielle, inflammatoire ou réactionnelle. Le bilan réalisé au cabinet d'Asnières-sur-Seine vise à l'identifier avant d'engager un traitement.",
        photo: { file: "mur.jpg", alt: "Mur du cabinet, ambiance chaleureuse et lumineuse" },
        condition: "Alopécie",
        sections: [
            {
                h2: "Les différents types de chute",
                paragraphes: [
                    "On distingue d'abord les chutes brutales et diffuses, survenant quelques mois après un événement déclenchant — accouchement, carence, régime, infection, choc émotionnel, arrêt d'une contraception — des chutes progressives, qui s'installent et modifient la ligne des cheveux.",
                    "Dans le second cas, la raie s'élargit sur le dessus du crâne tandis que les tempes restent longtemps préservées : c'est le profil classique d'une alopécie d'origine androgénique, fréquente en cas de SOPK ou après la ménopause.",
                ],
                puces: [
                    "raie qui s'élargit, cuir chevelu visible sous la lumière",
                    "queue de cheval nettement moins épaisse",
                    "cheveux plus fins, plus courts, qui ne poussent plus aussi longtemps",
                    "chute très abondante au lavage ou au brossage",
                    "démangeaisons ou sensation de cuir chevelu sensible",
                ],
            },
            {
                h2: "Le bilan",
                paragraphes: [
                    "La consultation reprend la chronologie de la chute, les antécédents familiaux, les traitements, l'alimentation et les événements des derniers mois. L'examen évalue la densité, la ligne frontale, l'état du cuir chevelu et la qualité des tiges.",
                    "Un bilan sanguin est le plus souvent prescrit : fer et ferritine, thyroïde, vitamine D, bilan hormonal selon le contexte. C'est lui qui permet de distinguer une carence corrigeable d'un mécanisme hormonal, et d'éviter des traitements inutiles.",
                ],
            },
            {
                h2: "Les traitements possibles",
                paragraphes: [
                    "La prise en charge corrige d'abord ce qui peut l'être : carence martiale, dysthyroïdie, déséquilibre hormonal. C'est la base, et elle suffit parfois à stopper une chute réactionnelle.",
                    "S'y ajoutent, selon les cas, un traitement local ou général prescrit par le médecin et des séances de mésothérapie du cuir chevelu, qui apportent directement au bulbe des principes actifs stimulant la repousse.",
                    "Le rythme est expliqué dès le départ : les cheveux poussent d'environ un centimètre par mois, l'évaluation d'un traitement capillaire se fait donc sur plusieurs mois, photographies à l'appui.",
                ],
            },
        ],
        faq: [
            [
                "Au bout de combien de temps voit-on un résultat ?",
                "Il faut compter plusieurs mois : la première phase consiste à stopper la chute, la repousse vient ensuite. Un suivi photographique au cabinet permet de juger objectivement de l'évolution.",
            ],
            [
                "La mésothérapie du cuir chevelu est-elle douloureuse ?",
                "Les micro-injections sont superficielles et brèves. La sensation est généralement bien tolérée, et la séance dure une vingtaine de minutes.",
            ],
            [
                "Une chute de cheveux peut-elle être le premier signe d'un SOPK ?",
                "Oui, surtout si elle s'accompagne d'acné, de cycles irréguliers ou d'une pilosité excessive. C'est l'une des raisons pour lesquelles le bilan capillaire inclut une évaluation hormonale.",
            ],
        ],
        related: ["sopk", "menopause", "epilation-laser"],
    },

    "acide-hyaluronique": {
        nav: "Acide hyaluronique",
        title: "Injections d'acide hyaluronique à Asnières-sur-Seine",
        description:
            "Injections d'acide hyaluronique et inducteurs de collagène à Asnières-sur-Seine : cernes, sillons, ovale du visage, lèvres. Actes réalisés par un médecin.",
        eyebrow: "Volumes et rides",
        h1: "Injections d'acide hyaluronique, à Asnières-sur-Seine",
        lead: "Redonner du soutien là où le visage s'est creusé, sans le transformer. Les injections sont réalisées par un médecin, après une consultation, avec un devis écrit et un délai de réflexion.",
        photo: { file: "accueil.jpg", alt: "L'accueil du cabinet Aurassium Clinic à Asnières-sur-Seine" },
        condition: "Vieillissement cutané",
        sections: [
            {
                h2: "Ce que l'acide hyaluronique fait, et ne fait pas",
                paragraphes: [
                    "L'acide hyaluronique est une molécule naturellement présente dans la peau, où elle retient l'eau et soutient les tissus. Injectée, elle comble un creux, redonne du volume ou hydrate en profondeur selon sa densité.",
                    "Elle ne tend pas la peau et ne remplace pas un geste chirurgical en cas de relâchement important. Elle n'empêche pas non plus le vieillissement : son effet est transitoire, de plusieurs mois à plus d'un an selon la zone et le produit.",
                    "Dire ce qu'un traitement ne fera pas fait partie de la consultation. C'est à cette condition que le résultat correspond à ce qui était annoncé.",
                ],
                puces: [
                    "cernes creux et vallée des larmes",
                    "sillons nasogéniens et plis d'amertume",
                    "pommettes et tempes",
                    "ovale du visage et menton",
                    "lèvres : hydratation ou redéfinition discrète",
                    "mains",
                ],
            },
            {
                h2: "Inducteurs de collagène : une autre logique",
                paragraphes: [
                    "Les inducteurs de collagène ne comblent pas : ils stimulent la production de collagène par la peau elle-même. Le résultat s'installe progressivement, sur plusieurs semaines, et agit sur la qualité et la fermeté plutôt que sur le volume.",
                    "Les deux approches sont souvent associées : l'acide hyaluronique restaure la structure là où elle manque, les inducteurs améliorent la texture et la tenue de la peau. Le plan est établi en consultation, zone par zone.",
                ],
            },
            {
                h2: "Déroulé, suites et sécurité",
                paragraphes: [
                    "La consultation précède toujours l'injection : antécédents, traitements en cours, attentes, contre-indications. Les produits utilisés sont des dispositifs médicaux, injectés dans un cadre médical.",
                    "La séance dure de quinze à trente minutes selon les zones. Une anesthésie de contact ou un produit contenant un anesthésique local est utilisé. Des ecchymoses et un œdème transitoires sont possibles : il est prudent de ne pas programmer une injection la veille d'un événement important.",
                    "Un rendez-vous de contrôle est proposé pour juger du résultat une fois l'œdème résorbé, et retoucher si nécessaire.",
                ],
            },
        ],
        faq: [
            [
                "Est-ce que cela va se voir ?",
                "L'objectif est que l'on vous trouve reposée, pas « refaite ». Les quantités sont adaptées à votre visage et peuvent être posées en plusieurs temps : il est toujours possible d'en ajouter, jamais d'en retirer facilement.",
            ],
            [
                "Combien de temps dure le résultat ?",
                "Selon la zone, le produit et votre métabolisme, l'effet se maintient généralement de plusieurs mois à plus d'un an. Un entretien est ensuite proposé, sans obligation.",
            ],
            [
                "Est-ce douloureux ?",
                "L'inconfort est limité grâce à l'anesthésie de contact et aux produits contenant un anesthésique local. Les zones les plus sensibles, comme les lèvres, font l'objet d'une préparation spécifique.",
            ],
        ],
        related: ["peau-et-rides", "melasma", "menopause"],
    },

    "peau-et-rides": {
        nav: "Peau et rides",
        title: "Traitement de la peau et des rides à Asnières-sur-Seine",
        description:
            "Qualité de peau et rides à Asnières-sur-Seine : peelings, microneedling, skinboosters, mésothérapie. Protocole médical sur mesure, par le Dr Souhali Massilia.",
        eyebrow: "Qualité de peau",
        h1: "Traitement de la peau et des rides",
        lead: "Teint terne, pores dilatés, ridules, peau qui marque : la qualité de peau se travaille progressivement, avec des protocoles adaptés à votre type de peau et à votre saison.",
        photo: { file: "mur.jpg", alt: "Ambiance lumineuse et chaleureuse du cabinet à Asnières-sur-Seine" },
        condition: "Vieillissement cutané",
        sections: [
            {
                h2: "Les techniques utilisées au cabinet",
                paragraphes: [
                    "Le peeling exfolie la peau de façon contrôlée pour relancer son renouvellement : superficiel pour le teint et les ridules, moyen pour les irrégularités plus marquées. Sa profondeur est choisie selon votre peau, jamais selon une formule standard.",
                    "Le microneedling crée de micro-canaux qui stimulent la production de collagène et améliorent la texture, les cicatrices d'acné et les pores dilatés.",
                    "Les skinboosters et la mésothérapie apportent directement dans le derme de l'acide hyaluronique très fluide, des vitamines et des antioxydants : la peau est repulpée de l'intérieur, sans modifier les volumes.",
                ],
                puces: [
                    "teint terne et manque d'éclat",
                    "ridules de déshydratation",
                    "pores dilatés et irrégularités de texture",
                    "cicatrices d'acné",
                    "peau qui marque et perd en fermeté",
                    "préparation de la peau avant un événement",
                ],
            },
            {
                h2: "Construire un protocole, pas empiler des séances",
                paragraphes: [
                    "La consultation évalue votre type de peau, sa sensibilité, votre exposition au soleil, votre routine et vos traitements en cours. Certaines techniques sont déconseillées sur une peau pigmentée ou sujette au mélasma : le protocole en tient compte.",
                    "Les séances sont espacées et réévaluées. L'intensité n'est pas un gage de résultat : une peau agressée se défend, s'inflamme et pigmente. La régularité et la photoprotection font davantage que la puissance d'un acte isolé.",
                    "Lorsque le relâchement dépasse ce que la qualité de peau peut corriger, les injections d'acide hyaluronique ou les inducteurs de collagène prennent le relais, en complément.",
                ],
            },
            {
                h2: "Après la séance",
                paragraphes: [
                    "Les suites dépendent de la technique : rougeur de quelques heures après un microneedling ou une mésothérapie, desquamation de quelques jours après un peeling moyen. Tout est annoncé avant, pour que vous puissiez choisir le bon moment.",
                    "Une protection solaire élevée est indispensable dans les suites, quelle que soit la saison : c'est elle qui protège le bénéfice obtenu et évite les taches post-inflammatoires.",
                ],
            },
        ],
        faq: [
            [
                "Combien de séances faut-il ?",
                "La plupart des protocoles de qualité de peau prévoient trois à quatre séances espacées de quelques semaines, puis un entretien. Le nombre exact est estimé en consultation, selon l'état de départ.",
            ],
            [
                "Peut-on traiter en été ?",
                "Certaines techniques le permettent avec une photoprotection stricte, d'autres sont reportées à l'automne. C'est un point systématiquement abordé, surtout en cas de peau pigmentée.",
            ],
            [
                "Quelle différence entre skinbooster et acide hyaluronique injecté ?",
                "Le skinbooster hydrate et améliore la qualité de la peau sans créer de volume ; l'acide hyaluronique de comblement, lui, restaure une structure ou un creux. Les deux peuvent être associés.",
            ],
        ],
        related: ["acide-hyaluronique", "melasma", "acne"],
    },

    "sante-intime": {
        nav: "Santé intime",
        title: "Sécheresse intime : traitement à Asnières-sur-Seine",
        description:
            "Sécheresse et atrophie intimes à Asnières-sur-Seine : consultation médicale, prise en charge de l'inconfort lié à la ménopause. Dr Souhali Massilia.",
        eyebrow: "Santé intime",
        h1: "Sécheresse intime : en parler, puis la traiter",
        lead: "La sécheresse intime touche une femme sur deux après la ménopause, et bien avant chez certaines. C'est un motif de consultation fréquent, qui se traite — et qui mérite mieux que le silence.",
        photo: { file: "cafe.jpg", alt: "Le coin café du cabinet, espace d'attente discret et chaleureux" },
        condition: "Atrophie vulvo-vaginale",
        sections: [
            {
                h2: "Des signes que l'on garde souvent pour soi",
                paragraphes: [
                    "La baisse des œstrogènes amincit la muqueuse et réduit la lubrification naturelle. S'installent alors une sensation de sécheresse, des tiraillements, des brûlures, un inconfort au quotidien et souvent des douleurs lors des rapports.",
                    "Ces signes ne sont ni une fatalité, ni une conséquence inévitable de l'âge. Ils apparaissent aussi après certains traitements, après un accouchement, pendant l'allaitement ou sous contraception, et s'aggravent sans prise en charge.",
                ],
                puces: [
                    "sécheresse et tiraillements au quotidien",
                    "brûlures ou démangeaisons",
                    "douleurs pendant les rapports",
                    "infections urinaires ou mycoses à répétition",
                    "sensation de relâchement ou d'inconfort",
                ],
            },
            {
                h2: "La consultation",
                paragraphes: [
                    "L'entretien précise depuis quand les signes existent, leur retentissement et le contexte hormonal. Un examen est proposé, jamais imposé, et expliqué à chaque étape.",
                    "La prise en charge est médicale : traitements locaux, hydratants spécifiques, traitement hormonal local lorsqu'il est indiqué et non contre-indiqué, et soins réalisés au cabinet pour améliorer la trophicité de la muqueuse.",
                    "Les options sont présentées avec leurs limites et leurs contre-indications, y compris en cas d'antécédent qui exclut certains traitements.",
                ],
            },
            {
                h2: "Un sujet traité comme un autre",
                paragraphes: [
                    "La consultation se déroule dans un cadre médical, sans jugement et sans euphémisme. Beaucoup de patientes attendent des années avant d'en parler, en pensant que cela ne se traite pas.",
                    "La sécheresse intime s'inscrit souvent dans un tableau plus large — ménopause, peau plus sèche, cheveux plus fins — pris en charge dans son ensemble au cabinet.",
                ],
            },
        ],
        faq: [
            [
                "Faut-il être ménopausée pour consulter ?",
                "Non. La sécheresse intime peut survenir bien plus tôt : après un accouchement, pendant l'allaitement, sous certains traitements ou contraceptions, ou sans cause hormonale évidente.",
            ],
            [
                "Les traitements sont-ils hormonaux ?",
                "Pas nécessairement. Il existe des prises en charge non hormonales, locales, et des soins réalisés au cabinet. Le choix dépend de vos antécédents et de vos préférences.",
            ],
            [
                "Est-ce que cela peut s'améliorer durablement ?",
                "Oui, à condition d'un suivi : la muqueuse se fragilise de nouveau si la prise en charge est interrompue. Un entretien est donc souvent prévu.",
            ],
        ],
        related: ["menopause", "peau-et-rides", "sopk"],
    },

    acne: {
        nav: "Acné",
        title: "Acné de la femme adulte : médecin à Asnières-sur-Seine",
        description:
            "Acné adulte, acné hormonale et cicatrices : bilan et traitement médical à Asnières-sur-Seine. Consultation du Dr Souhali Massilia (92600).",
        eyebrow: "Peau",
        h1: "Acné de la femme adulte : chercher la cause hormonale",
        lead: "Une acné qui apparaît ou persiste après 25 ans n'est pas une acné d'adolescente attardée. Chez la femme adulte, elle est très souvent liée aux hormones — et c'est de là que part la prise en charge.",
        photo: { file: "bureau.jpg", alt: "Cabine de soins du cabinet à Asnières-sur-Seine" },
        condition: "Acné",
        sections: [
            {
                h2: "Reconnaître une acné hormonale",
                paragraphes: [
                    "Elle siège volontiers sur le bas du visage : mâchoire, menton, cou. Les lésions sont souvent profondes, inflammatoires, douloureuses, et s'aggravent dans la seconde partie du cycle.",
                    "Lorsqu'elle s'accompagne de cycles irréguliers, d'une pilosité excessive ou d'une chute de cheveux, l'hypothèse d'un excès d'androgènes — notamment un SOPK — doit être explorée. Traiter la peau sans explorer ce terrain expose à la récidive à l'arrêt du traitement.",
                ],
                puces: [
                    "lésions sur la mâchoire, le menton et le cou",
                    "poussées rythmées par le cycle",
                    "lésions profondes et douloureuses",
                    "marques et cicatrices persistantes",
                    "association à une pilosité ou une chute de cheveux",
                ],
            },
            {
                h2: "Le bilan et le traitement",
                paragraphes: [
                    "La consultation reprend l'histoire de l'acné, les traitements déjà essayés, la contraception et la routine de soins. Un bilan hormonal est prescrit lorsque le tableau le justifie.",
                    "Le traitement associe une prise en charge médicale de l'acné elle-même et, lorsque c'est nécessaire, celle du déséquilibre hormonal sous-jacent. Les routines inadaptées, fréquentes, sont corrigées : une peau acnéique décapée devient plus réactive et marque davantage.",
                    "Une fois l'inflammation contrôlée, les marques et les cicatrices peuvent être travaillées au cabinet, par microneedling ou peelings adaptés — jamais sur une acné encore active.",
                ],
            },
            {
                h2: "Les marques laissées par l'acné",
                paragraphes: [
                    "Il faut distinguer les taches post-inflammatoires, brunes ou rouges, qui s'estompent avec le temps et la photoprotection, des cicatrices en creux, qui nécessitent un traitement spécifique.",
                    "La protection solaire est déterminante : sur une peau acnéique, le soleil fonce durablement les marques. C'est le premier conseil donné en consultation, avant toute séance.",
                ],
            },
        ],
        faq: [
            [
                "Faut-il arrêter sa contraception ?",
                "Pas sans avis médical. Certaines contraceptions aggravent l'acné, d'autres l'améliorent : la décision se prend en consultation, en tenant compte de l'ensemble de votre situation.",
            ],
            [
                "Au bout de combien de temps voit-on une amélioration ?",
                "Un traitement de l'acné s'évalue sur plusieurs semaines à plusieurs mois. Les premières semaines peuvent même s'accompagner d'une phase de poussée, annoncée à l'avance.",
            ],
            [
                "Peut-on traiter les cicatrices en même temps ?",
                "Non. On contrôle d'abord l'inflammation, puis on traite les marques. Travailler une peau encore active expose à aggraver la pigmentation.",
            ],
        ],
        related: ["sopk", "peau-et-rides", "melasma"],
    },
};

export default soinsFr;
